import { useEffect, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/ui/Icon';

interface SheetProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  /** Architectural section marker shown above the title. */
  eyebrow?: ReactNode;
  children: ReactNode;
  /** Footer slot — typically a primary CTA + secondary outline. */
  footer?: ReactNode;
  /** Tailwind max-height class — defaults to a comfortable drawer height. */
  maxHeight?: string;
  className?: string;
}

const EASE = [0.19, 1, 0.22, 1] as const;

/**
 * Bottom sheet primitive — rolled with Framer Motion (no vaul dep).
 *
 * - Drag-to-dismiss (down only): pulling > 120px or releasing with velocity
 *   triggers close. Anything less snaps back.
 * - 12px top radius (the canonical exception); 2px elsewhere.
 * - Pink hairline along the top edge as the brand signal.
 * - Ink scrim with backdrop-blur. ESC + scrim-tap close.
 * - Safe-area aware on mobile; centered card on desktop (md+).
 */
export const Sheet = ({
  open,
  onClose,
  title,
  eyebrow,
  children,
  footer,
  maxHeight = 'max-h-[85dvh]',
  className,
}: SheetProps) => {
  // ESC to dismiss
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Lock background scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Scrim */}
          <motion.div
            key="sheet-scrim"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: EASE }}
            onClick={onClose}
            className="fixed inset-0 z-50 bg-night/80 backdrop-blur-sm"
          />

          {/* Panel */}
          <motion.section
            key="sheet-panel"
            role="dialog"
            aria-modal="true"
            aria-label={typeof title === 'string' ? title : 'Sheet'}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ duration: 0.4, ease: EASE }}
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.4 }}
            onDragEnd={(_, info) => {
              if (info.offset.y > 120 || info.velocity.y > 600) onClose();
            }}
            className={cn(
              // Position
              'fixed inset-x-0 bottom-0 z-50',
              // Mobile / tablet: full-bleed bottom sheet
              'mx-auto md:inset-x-auto md:bottom-auto md:left-1/2 md:top-1/2',
              'md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-lg',
              // Surface
              'rounded-t-[12px] md:rounded-[2px]',
              'border-t border-pink/30 md:border md:border-white/[0.10]',
              'bg-ink text-text-primary shadow-glow-cast',
              maxHeight,
              'flex flex-col',
              'pb-[env(safe-area-inset-bottom)]',
              className,
            )}
          >
            {/* Drag handle (mobile only) */}
            <div className="flex justify-center pt-3 md:hidden">
              <span aria-hidden className="h-1 w-10 rounded-full bg-white/15" />
            </div>

            {/* Header */}
            {(title || eyebrow) && (
              <header className="flex items-start justify-between gap-4 px-6 py-5 md:py-6">
                <div className="min-w-0">
                  {eyebrow && (
                    <p className="mb-2 inline-flex items-center gap-3 font-display text-[10px] font-bold uppercase tracking-widest text-pink">
                      <span aria-hidden className="h-px w-7 bg-pink" />
                      {eyebrow}
                    </p>
                  )}
                  {title && (
                    <h2 className="font-display text-2xl font-black uppercase leading-none tracking-display">
                      {title}
                    </h2>
                  )}
                </div>
                <button
                  onClick={onClose}
                  aria-label="Close"
                  className="flex h-8 w-8 shrink-0 items-center justify-center text-text-muted transition-colors duration-fast ease-signal hover:text-pink"
                >
                  <Icon name="close" size={20} />
                </button>
              </header>
            )}

            {/* Body */}
            <div className="hottr-no-scrollbar flex-1 overflow-y-auto px-6 pb-6">
              {children}
            </div>

            {/* Footer */}
            {footer && (
              <footer className="border-t border-white/[0.07] bg-ink/95 px-6 py-4 backdrop-blur">
                {footer}
              </footer>
            )}
          </motion.section>
        </>
      )}
    </AnimatePresence>
  );
};
